import { FormikProps } from "formik";
import { PromotionType } from "../../../interfaces/promotionType";
import SelectFilter from "../../../components/common/Inputs/SelectFilter";
import { useMemo, useState } from "react";
import Button from "../../../components/common/Buttons/Button";
import { FaRegTimesCircle, FaSave } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { PromotionTargetsTable } from "./PromotionTargetsTable";
import { useProductsDataQuery } from "../../products/queries/useProductsQuery";
import { useCategoriesQuery } from "../../categories/queries/useCategoriesQuery";

type PromotionTargetsFormProps = {
  formik: FormikProps<PromotionType>;
  discountTarget: "product-category" | "product";
};

export const PromotionTargetsForm = (props: PromotionTargetsFormProps) => {
  const { formik, discountTarget } = props;
  const [selectedId, setSelectedId] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { data: productsData } = useProductsDataQuery({
    enabled: discountTarget === "product",
  });
  const { data: categoriesData } = useCategoriesQuery({
    enabled: discountTarget === "product-category",
  });

  const { filterTable, filterData } = useMemo(() => {
    if (discountTarget === "product-category") {
      return {
        filterTable: "Categoría de Producto",
        filterData: categoriesData?.data || [],
      };
    }
    return {
      filterTable: "Producto Específico",
      filterData: productsData?.data || [],
    };
  }, [discountTarget, productsData, categoriesData]);

  const handleProductOrderOnChange = (productId: string) => {
    setSelectedId(productId);
  };

  const handleAddItem = (itemId: string) => () => {
    const currentItems = formik.values.targetItemsId || [];
    if (isEditing && currentItems.includes(itemId)) {
      const updatedItems = currentItems.map((id) =>
        id === itemId ? itemId : id,
      );
      formik.setFieldValue("targetItemsId", updatedItems);
      setIsEditing(false);
      return;
    }
    if (!currentItems.includes(itemId)) {
      const updatedItems = [...currentItems, itemId];
      formik.setFieldValue("targetItemsId", updatedItems);
    }
  };

  const handleClearForm = () => {
    setSelectedId("");
    setIsEditing(false);
  };

  const handleEdit = (itemId: string) => {
    setSelectedId(itemId);
    setIsEditing(true);
  };

  const handleDelete = (itemId: string) => {
    const currentItems = formik.values.targetItemsId || [];
    const updatedItems = currentItems.filter((id) => id !== itemId);
    formik.setFieldValue("targetItemsId", updatedItems);
    if (isEditing && selectedId === itemId) {
      handleClearForm();
    }
  };

  return (
    <div className="border-t-2 border-gray-200 mt-2 pt-2">
      <div className="grid grid-cols-2 gap-4">
        <SelectFilter
          label={filterTable}
          data={filterData}
          dataKey={"_id"}
          dataLabel={"name"}
          selected={selectedId}
          onSelect={handleProductOrderOnChange}
          error={
            formik.touched.targetItemsId &&
            formik.errors.targetItemsId &&
            Array.isArray(formik.errors.targetItemsId)
              ? formik.errors.targetItemsId.join(", ")
              : formik.errors.targetItemsId
          }
        />
        <div className="flex gap-4 items-end mb-2">
          <Button
            size="medium"
            color="primary"
            className="inline-block mt-2 h-11"
            onClick={handleAddItem(selectedId)}
          >
            {isEditing ? <FaSave /> : <FaPlus />}
          </Button>
          {isEditing && (
            <Button
              size="medium"
              color="secondary"
              className="inline-block mt-2 ml-2 h-11"
              onClick={handleClearForm}
            >
              <FaRegTimesCircle />
            </Button>
          )}
        </div>
      </div>
      <PromotionTargetsTable
        targetItemsId={formik.values.targetItemsId || []}
        discountTarget={discountTarget}
        onEdit={handleEdit}
        onDelete={handleDelete}
        discountPercentage={formik.values.discountPercentage ?? 0}
      />
    </div>
  );
};
